import { useState } from "react"
import { countries } from "../../../utils/countryData"
import FileUploadBox from "../../ui/FileUploadBox"
import { uploadDocument } from "../../../services/documentService"

export default function NewPriorityForm({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void
  onSubmit: (data: {
    office: string
    filingDate: string
    filingNumber: string
    euipoDocumentId?: string | null
    originalFileName?: string | null
  }) => void
}) {
  const [country, setCountry] = useState("")
  const [filingDate, setFilingDate] = useState("")
  const [applicationNumber, setApplicationNumber] = useState("")
  const [copyFile, setCopyFile] = useState<File | null>(null)
  const [errorCopy, setErrorCopy] = useState("")
  const [loading, setLoading] = useState(false)

  const [filingDateError, setFilingDateError] = useState("")
  const [fileNameError, setFileNameError] = useState("")

  const today = new Date().toISOString().split("T")[0]

  const sixMonthsAgo = new Date()
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

  const validateFilingDate = (date: string) => {

    if (!date) return false

    const selectedDate = new Date(date)

    if (date > today) {
      setFilingDateError("Filing date cannot be in the future")
      return false
    }

    if (selectedDate < sixMonthsAgo) {
      setFilingDateError("Priority date must not be older than 6 months")
      return false
    }

    setFilingDateError("")
    return true
  }

  const validateFileName = (file: File | null) => {
    if (file && file.name.length > 25) {
      setFileNameError("File name cannot exceed 25 characters")
      return false
    }
    setFileNameError("")
    return true
  }

  const handleSubmit = async () => {
    if (!copyFile || filingDateError || fileNameError) return

    setLoading(true)

    try {
      const result = await uploadDocument(copyFile, "PRIORITY_COPY")

      onSubmit({
        office: country,
        filingDate,
        filingNumber: applicationNumber,
        euipoDocumentId: result.euipoDocumentId,
        originalFileName: result.originalFileName,
      })
    } catch {
      setErrorCopy("Document upload failed")
    } finally {
      setLoading(false)
    }
  }

  const isFormValid =
    country &&
    filingDate &&
    applicationNumber &&
    copyFile &&
    !filingDateError &&
    !fileNameError

  return (
    <div className="mt-4 border-t pt-4 space-y-6 text-sm">
      <div>
        <label className="block mb-1 font-medium">
          Country of the first filing *
        </label>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="w-full border rounded p-1.5"
        >
          <option value="">– Select –</option>
          {countries.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block mb-1 font-medium">Filing date *</label>
          <input
            type="date"
            value={filingDate}
            onChange={(e) => {
              const v = e.target.value
              setFilingDate(v)
              validateFilingDate(v)
            }}
            className="w-full border rounded p-1.5"
          />
          {filingDateError && (
            <p className="text-red-600 text-xs mt-1">
              {filingDateError}
            </p>
          )}
        </div>

        <div>
          <label className="block mb-1 font-medium">
            Application number *
          </label>
          <input
            type="text"
            value={applicationNumber}
            onChange={(e) => setApplicationNumber(e.target.value)}
            className="w-full border rounded p-1.5"
          />
        </div>
      </div>

      <div className="border-t pt-3">
        <p className="font-medium mb-2">Copy of the first filing *</p>
        <FileUploadBox
          uploadedFile={copyFile}
          error={errorCopy}
          onFileSelect={(file) => {
            setCopyFile(file)
            setErrorCopy("")
            validateFileName(file)
          }}
          onDelete={() => {
            setCopyFile(null)
            setFileNameError("")
          }}
        />
        {fileNameError && (
          <p className="text-red-600 text-xs mt-1">
            {fileNameError}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-3 border-t">
        <button
          onClick={onCancel}
          className="text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          disabled={!isFormValid || loading}
          onClick={handleSubmit}
          className="px-3 py-1.5 rounded text-sm font-medium bg-blue-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Add priority
        </button>
      </div>
    </div>
  )
}